import { useState } from "react";

import { Loader2, Lightbulb, TrendingUp, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

interface TemplateSuggestion {
  name: string;
  description: string;
  targetAudience: string;
  industry: string;
  keyPersonas: string[];
  trendReason: string;
  priority: "high" | "medium" | "low";
}

interface BoardTemplateSuggestionsProps {
  onSelectTemplate: (suggestion: TemplateSuggestion) => void;
}

export const BoardTemplateSuggestions = ({ onSelectTemplate }: BoardTemplateSuggestionsProps) => {
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [suggestions, setSuggestions] = useState<TemplateSuggestion[]>([]);

  const generateSuggestions = async () => {
    const { data, error } = await invoke("suggest-board-templates", {
      showErrorToast: true,
    });

    if (error) return;
    
    setSuggestions(data.suggestions);
    toast.success(`Generated ${data.suggestions.length} template suggestions!`);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "default";
      case "medium":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "outline";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              AI Template Suggestions
            </CardTitle>
            <CardDescription>
              Discover trending board templates based on industry insights and user analytics
            </CardDescription>
          </div>
          <Button onClick={generateSuggestions} disabled={isLoading} size="sm">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                Get Suggestions
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {suggestions.length > 0 && (
        <CardContent>
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        {suggestion.name}
                        <Badge variant={getPriorityColor(suggestion.priority)}>
                          {suggestion.priority}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {suggestion.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Target Audience:</span>
                      <p className="text-muted-foreground">{suggestion.targetAudience}</p>
                    </div>
                    <div>
                      <span className="font-medium">Industry:</span>
                      <p className="text-muted-foreground">{suggestion.industry}</p>
                    </div>
                  </div>

                  <div>
                    <span className="font-medium text-sm flex items-center gap-1 mb-2">
                      <Users className="h-3 w-3" />
                      Key Personas:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {suggestion.keyPersonas.map((persona, pIndex) => (
                        <Badge key={pIndex} variant="outline">
                          {persona}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex items-start gap-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{suggestion.trendReason}</span>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => onSelectTemplate(suggestion)}
                  >
                    Create from Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
